import React, { useState } from "react";

// Security helper functions
const getApiBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL || "";
  // Validate API URL doesn't have suspicious patterns
  if (
    apiUrl &&
    !apiUrl.startsWith("http://") &&
    !apiUrl.startsWith("https://")
  ) {
    return "";
  }
  return apiUrl;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("ogestyle_token");
  if (!token) {
    return null;
  }
  return {
    Authorization: `Bearer ${token}`,
  };
};

/**
 * Step 2: CSV Upload Interface
 * User uploads CSV files for services, customers, appointments, and staff
 * Calls backend API to validate and detect conflicts
 * For freelancers, staff upload is hidden (they are their own staff)
 */
function Step2Upload({
  migrationData,
  updateMigrationData,
  nextStep,
  prevStep,
  type, // 'business' or 'freelancer'
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState({});
  const [processingStatus, setProcessingStatus] = useState("");

  // Determine entity type from migrationData or prop
  const entityType = type || migrationData.type || "business";
  const isFreelancer = entityType === "freelancer";

  const allFileTypes = [
    {
      id: "services",
      name: "Services",
      icon: "✂️",
      description: "Your service menu (haircuts, treatments, etc.)",
      required: true,
      example: "services.csv",
    },
    {
      id: "customers",
      name: "Customers",
      icon: "👥",
      description:
        "Customer contact information and history (required to notify them)",
      required: true,
      example: "customers.csv",
    },
    // DISABLED: Appointment import removed for new business/freelancer migrations
    //   {
    //     id: "appointments",
    //     name: "Appointments",
    //     icon: "📅",
    //     description: "Past and future bookings",
    //     required: false,
    //     example: "appointments.csv",
    //     businessOnly: true, // Hide for freelancers - use dedicated Bookings import instead
    //   },
    {
      id: "staff",
      name: "Staff/Team",
      icon: "👤",
      description: "Your team members and their roles",
      required: false,
      example: "staff.csv",
      businessOnly: true, // Hide for freelancers
    },
  ];

  // Filter out staff option for freelancers
  const fileTypes = isFreelancer
    ? allFileTypes.filter((ft) => !ft.businessOnly)
    : allFileTypes;

  const handleFileSelect = (fileType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith(".csv")) {
      setUploadErrors({
        ...uploadErrors,
        [fileType]: "Please upload a CSV file",
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setUploadErrors({
        ...uploadErrors,
        [fileType]: "File too large. Maximum size is 10MB",
      });
      return;
    }

    // Clear any previous errors
    const newErrors = { ...uploadErrors };
    delete newErrors[fileType];
    setUploadErrors(newErrors);

    // Update migration data
    updateMigrationData({
      uploadedFiles: {
        ...migrationData.uploadedFiles,
        [fileType]: file,
      },
    });
  };

  const removeFile = (fileType) => {
    const newFiles = { ...migrationData.uploadedFiles };
    newFiles[fileType] = null;
    updateMigrationData({ uploadedFiles: newFiles });

    // Clear error
    const newErrors = { ...uploadErrors };
    delete newErrors[fileType];
    setUploadErrors(newErrors);
  };

  const handleContinue = async () => {
    // Check if required files are uploaded
    const servicesFile = migrationData.uploadedFiles.services;
    const customersFile = migrationData.uploadedFiles.customers;

    if (!servicesFile || !customersFile) {
      alert(
        "Please upload both Services and Customers CSV files to continue. Customers are required to notify them about the new platform.",
      );
      return;
    }

    const authHeaders = getAuthHeaders();
    if (!authHeaders) {
      alert("Session expired. Please log in again.");
      window.location.href = "/login";
      return;
    }

    setUploading(true);
    setProcessingStatus("Preparing files...");

    try {
      const apiBaseUrl = getApiBaseUrl();
      const entityType = migrationData.type || "business";
      const entityId = migrationData.businessId || migrationData.staffId;

      // Store all validation results and conflicts
      const validationResults = {};
      const allConflicts = [];
      const allMappings = {};

      // Process each uploaded file
      const uploadedFileTypes = Object.entries(
        migrationData.uploadedFiles,
      ).filter(([_, file]) => file !== null);

      for (const [dataType, file] of uploadedFileTypes) {
        setProcessingStatus(`Processing ${dataType}...`);

        const formData = new FormData();
        formData.append("file", file);
        formData.append("dataType", dataType);
        formData.append(
          "sourcePlatform",
          migrationData.sourcePlatform || "generic",
        );

        // Add existing mapping if available
        if (
          migrationData.fieldMappings[dataType] &&
          Object.keys(migrationData.fieldMappings[dataType]).length > 0
        ) {
          formData.append(
            "mapping",
            JSON.stringify(migrationData.fieldMappings[dataType]),
          );
        }

        const response = await fetch(
          `${apiBaseUrl}/api/import/${entityType}/${entityId}/prepare-import`,
          {
            method: "POST",
            headers: authHeaders,
            body: formData,
          },
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error || `Failed to process ${dataType} file`,
          );
        }

        const result = await response.json();

        // Store validation result
        validationResults[dataType] = {
          fileInfo: result.fileInfo,
          validation: result.validation,
          dataPreview: result.dataPreview,
          metadata: result.metadata,
        };

        // Collect mappings (auto-detected or from suggestions)
        if (result.mapping) {
          allMappings[dataType] = result.mapping;
        } else if (result.suggestions?.mapping) {
          allMappings[dataType] = result.suggestions.mapping;
        }

        // Collect conflicts
        if (result.conflicts?.details && result.conflicts.details.length > 0) {
          // Preserve original backend ID (staff_1, customer_0, etc.) — these IDs match
          // the CSV row index that importEngine uses when applying conflict resolutions.
          // DO NOT override with conflict-array index or the wrong records get skipped.
          const conflictsWithType = result.conflicts.details.map((c) => ({
            ...c,
            dataType,
            canProceed: result.conflicts.canProceed,
          }));
          allConflicts.push(...conflictsWithType);
        }
      }

      setProcessingStatus("All files processed successfully!");

      // Update migration data with results
      updateMigrationData({
        validationResults,
        conflicts: allConflicts,
        fieldMappings: {
          ...migrationData.fieldMappings,
          ...allMappings,
        },
        // Store raw conflicts for Step 4
        conflictData: {
          total: allConflicts.length,
          byDataType: uploadedFileTypes.reduce((acc, [type]) => {
            acc[type] = allConflicts.filter((c) => c.dataType === type).length;
            return acc;
          }, {}),
        },
      });

      setUploading(false);
      nextStep();
    } catch (error) {
      setUploadErrors({
        general: error.message || "Failed to process files. Please try again.",
      });
      setUploading(false);
      setProcessingStatus("");
    }
  };

  const getPlatformName = () => {
    const platforms = {
      square: "Square",
      squire: "Squire",
      booksy: "Booksy",
      other: "your platform",
    };
    return platforms[migrationData.sourcePlatform] || "your platform";
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.heading}>Upload Your CSV Files</h2>
        <p style={styles.subheading}>
          Upload the CSV files you exported from {getPlatformName()}. We'll
          automatically detect the data structure and map your fields.
        </p>
        {isFreelancer && (
          <div style={styles.freelancerNote}>
            ℹ️ <strong>Freelancer Import:</strong> All services and appointments
            will be linked to your profile directly. No staff import is needed.
          </div>
        )}
      </div>

      <div style={styles.uploadSection}>
        {fileTypes.map((type) => {
          const file = migrationData.uploadedFiles[type.id];
          const error = uploadErrors[type.id];

          return (
            <div key={type.id} style={styles.fileCard}>
              <div style={styles.fileHeader}>
                <div style={styles.fileIcon}>{type.icon}</div>
                <div style={styles.fileInfo}>
                  <h4 style={styles.fileName}>
                    {type.name}
                    {type.required && (
                      <span style={styles.requiredBadge}>Required</span>
                    )}
                  </h4>
                  <p style={styles.fileDescription}>{type.description}</p>
                </div>
              </div>

              {!file ? (
                <div style={styles.uploadArea}>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => handleFileSelect(type.id, e)}
                    style={styles.fileInput}
                    id={`file-${type.id}`}
                  />
                  <label htmlFor={`file-${type.id}`} style={styles.uploadLabel}>
                    <div style={styles.uploadIcon}>📁</div>
                    <div style={styles.uploadText}>
                      Click to browse or drag and drop
                    </div>
                    <div style={styles.uploadHint}>
                      CSV files only • Max 10MB
                    </div>
                  </label>
                </div>
              ) : (
                <div style={styles.uploadedFile}>
                  <div style={styles.uploadedInfo}>
                    <div style={styles.uploadedIcon}>✓</div>
                    <div>
                      <div style={styles.uploadedName}>{file.name}</div>
                      <div style={styles.uploadedSize}>
                        {(file.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(type.id)}
                    style={styles.removeButton}
                  >
                    Remove
                  </button>
                </div>
              )}

              {error && <div style={styles.errorMessage}>⚠️ {error}</div>}
            </div>
          );
        })}
      </div>

      <div style={styles.helpSection}>
        <h3 style={styles.helpTitle}>
          💡 Need Help Exporting from {getPlatformName()}?
        </h3>
        <div style={styles.helpContent}>
          <p style={styles.helpText}>Follow these steps to export your data:</p>
          <ol style={styles.helpList}>
            <li>Log into your {getPlatformName()} account</li>
            <li>Navigate to Settings → Data Export or Reports</li>
            <li>Select the data types you want to export</li>
            <li>Download the CSV files</li>
            <li>Upload them here</li>
          </ol>
          <p style={styles.helpNote}>
            <strong>Tip:</strong> You can upload files one at a time or all at
            once. Only Services is required - the rest are optional but
            recommended for a complete migration.
          </p>
        </div>
      </div>

      {uploadErrors.general && (
        <div style={styles.generalError}>⚠️ {uploadErrors.general}</div>
      )}

      {uploading && processingStatus && (
        <div style={styles.processingStatus}>
          <div style={styles.spinner}></div>
          <span>{processingStatus}</span>
        </div>
      )}

      <div style={styles.actions}>
        <button
          onClick={prevStep}
          style={styles.backButton}
          disabled={uploading}
        >
          ← Back
        </button>
        <button
          onClick={handleContinue}
          disabled={
            !migrationData.uploadedFiles.services ||
            !migrationData.uploadedFiles.customers ||
            uploading
          }
          style={{
            ...styles.continueButton,
            ...(!migrationData.uploadedFiles.services ||
            !migrationData.uploadedFiles.customers ||
            uploading
              ? styles.continueButtonDisabled
              : {}),
          }}
        >
          {uploading
            ? processingStatus || "Processing..."
            : "Continue to Mapping →"}
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "32px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  header: {
    marginBottom: "32px",
  },
  heading: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#333",
    marginBottom: "12px",
  },
  subheading: {
    fontSize: "16px",
    color: "#666",
    lineHeight: 1.6,
  },
  freelancerNote: {
    marginTop: "16px",
    padding: "12px 16px",
    backgroundColor: "#f0f9ff",
    border: "1px solid #bae6fd",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#0369a1",
  },
  uploadSection: {
    display: "grid",
    gap: "20px",
    marginBottom: "32px",
  },
  fileCard: {
    border: "2px solid #e0e0e0",
    borderRadius: "12px",
    padding: "20px",
    transition: "border-color 0.3s ease",
  },
  fileHeader: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: "16px",
  },
  fileIcon: {
    fontSize: "32px",
    marginRight: "16px",
  },
  fileInfo: {
    flex: 1,
  },
  fileName: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#333",
    marginBottom: "4px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  requiredBadge: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#fff",
    backgroundColor: "#ff5722",
    padding: "2px 8px",
    borderRadius: "12px",
  },
  fileDescription: {
    fontSize: "14px",
    color: "#666",
  },
  uploadArea: {
    position: "relative",
  },
  fileInput: {
    display: "none",
  },
  uploadLabel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px",
    border: "2px dashed #ccc",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    backgroundColor: "#fafafa",
  },
  uploadIcon: {
    fontSize: "48px",
    marginBottom: "12px",
  },
  uploadText: {
    fontSize: "16px",
    color: "#333",
    marginBottom: "4px",
  },
  uploadHint: {
    fontSize: "14px",
    color: "#999",
  },
  uploadedFile: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px",
    backgroundColor: "#e8f5e9",
    borderRadius: "8px",
    border: "1px solid #4CAF50",
  },
  uploadedInfo: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  uploadedIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: "#4CAF50",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
  },
  uploadedName: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#333",
  },
  uploadedSize: {
    fontSize: "12px",
    color: "#666",
  },
  removeButton: {
    padding: "6px 16px",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    color: "#666",
  },
  errorMessage: {
    marginTop: "8px",
    padding: "8px 12px",
    backgroundColor: "#ffebee",
    color: "#c62828",
    borderRadius: "6px",
    fontSize: "14px",
  },
  helpSection: {
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "32px",
  },
  helpTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#333",
    marginBottom: "16px",
  },
  helpContent: {
    fontSize: "14px",
    color: "#666",
  },
  helpText: {
    marginBottom: "12px",
  },
  helpList: {
    marginLeft: "20px",
    marginBottom: "12px",
  },
  helpNote: {
    backgroundColor: "#fff",
    padding: "12px",
    borderRadius: "6px",
    marginTop: "12px",
  },
  generalError: {
    marginTop: "16px",
    marginBottom: "16px",
    padding: "12px 16px",
    backgroundColor: "#ffebee",
    color: "#c62828",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
  },
  processingStatus: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginTop: "16px",
    marginBottom: "16px",
    padding: "12px 16px",
    backgroundColor: "#e3f2fd",
    color: "#1565c0",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
  },
  spinner: {
    width: "20px",
    height: "20px",
    border: "3px solid #90caf9",
    borderTop: "3px solid #1565c0",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "32px",
  },
  backButton: {
    padding: "12px 24px",
    backgroundColor: "#fff",
    color: "#666",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
  },
  continueButton: {
    padding: "12px 32px",
    backgroundColor: "#2196F3",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
  },
  continueButtonDisabled: {
    backgroundColor: "#ccc",
    cursor: "not-allowed",
  },
};

export default Step2Upload;
